<template>
  <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 pb-40 h-fit">
    <router-link
      to="/customer"
      class="text-white col-span-2 lg:col-span-4"
      id="back"
    >
      &#10094; Back
    </router-link>
    <PageHeader
      title="Customer Detail"
      :subtitle="customer?.name"
      class="col-span-2 sm:col-span-2 lg:col-span-2"
    />

    <Card card_class="lg:row-start-3 col-span-2 md:col-span-1 lg:col-span-1">
      <div class="flex flex-col gap-4 w-full">
        <div
          v-for="(field, index) in customerDetails"
          :key="index"
          class="flex flex-col gap-1 w-full"
        >
          <h3 class="font-light text-white mb-1 text-sm">{{ field.label }}:</h3>
          <p class="text-white font-medium text-xl">{{ field.value }}</p>
        </div>
      </div>
    </Card>

    <Card
      card_class="lg:row-start-3 col-span-2 md:col-span-1 lg:col-span-2 max-h-96 overflow-y-auto"
    >
      <template v-if="coupons.length > 0">
        <!-- Available Coupons -->
        <div class="available-coupons mb-4">
          <h2 class="text-lg font-semibold text-white mb-2">
            Available Coupons
          </h2>
          <div v-if="availableCoupons.length > 0" class="flex flex-col gap-2">
            <template v-for="(coupon, index) in availableCoupons" :key="index">
              <Coupon
                :coupon_discount="coupon.discount"
                :coupon_expired_date="coupon.expired_date"
                :coupon_product_name="coupon.Product?.products_name"
                :coupon_max_usage="coupon.max_usage"
                :coupon_usage_count="coupon.usage_count"
                :is_expired="false"
              />
            </template>
          </div>
          <p v-else class="text-center text-white">No available coupons.</p>
        </div>

        <!-- Expired Coupons -->
        <div class="expired-coupons">
          <h2 class="text-lg font-semibold text-white mb-2">Expired Coupons</h2>
          <div v-if="expiredCoupons.length > 0" class="flex flex-col gap-2">
            <template v-for="(coupon, index) in expiredCoupons" :key="index">
              <Coupon
                :coupon_discount="coupon.discount"
                :coupon_expired_date="coupon.expired_date"
                :coupon_product_name="coupon.Product?.products_name"
                :coupon_max_usage="coupon.max_usage"
                :coupon_usage_count="coupon.usage_count"
                :is_expired="true"
              />
            </template>
          </div>
          <p v-else class="text-center text-white">No expired coupons.</p>
        </div>
      </template>

      <p v-else class="text-center text-white">No coupons available.</p>
    </Card>

    <Card
      card_class="col-span-2 md:col-span-2 lg:col-span-4"
      card_title="Transaction History"
      card_body_class="max-h-96 overflow-y-auto"
    >
      <DinamicTable :header_columns="table_header" th_class="text-left">
        <template v-if="transactions.length > 0">
          <TableRow v-for="(transaction, index) in transactions" :key="index">
            <TableCell>{{ index + 1 }}</TableCell>
            <TableCell>{{ transaction.id }}</TableCell>
            <TableCell>{{
              formatDate(transaction.transaction_date)
            }}</TableCell>
            <TableCell class="flex gap-1 flex-wrap items-center">
              <span
                class="bg-white/30 px-2 py-1 rounded-md"
                v-for="(product, index) in transaction.ProductTransactions"
                :key="index"
              >
                <p class="font-semibold text-xs">
                  {{ product?.Product?.products_name }}
                  <span class="font-normal">x{{ product?.quantity }}</span>
                </p>
              </span>
            </TableCell>
            <TableCell>{{ formatRupiah(transaction.grand_total) }}</TableCell>
            <TableCell>{{ getBranchName(transaction.branch_id) }}</TableCell>
          </TableRow>
        </template>
        <template v-else>
          <TableRow>
            <TableCell
              :colspan="table_header.length"
              class="text-center text-white"
            >
              No transactions available
            </TableCell>
          </TableRow>
        </template>
      </DinamicTable>
    </Card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import PageHeader from "@/components/molecules/PageHeader.vue";
import Card from "@/components/organisms/Card.vue";
import DinamicTable from "@/components/organisms/DinamicTable.vue";
import TableRow from "@/components/atoms/table/TableRow.vue";
import TableCell from "@/components/atoms/table/TableCell.vue";
import { formatRupiah, formatDate } from "@/helpers/formating";
import Coupon from "@/components/molecules/Coupon.vue";

export default {
  name: "CustomerView",
  props: ["id"],
  components: {
    PageHeader,
    DinamicTable,
    Card,
    TableRow,
    TableCell,
    Coupon,
  },
  data() {
    return {
      table_header: [
        "No",
        "Transaction ID",
        "Transaction Date",
        "Product",
        "Grand Total",
        "Branch",
      ],
      customer: null,
      transactions: [],
      coupons: [],
      loading: true,
      error: null,
    };
  },
  async created() {
    try {
      this.loading = true;

      const customerResponse = await this.fetchCustomerById(this.id);
      this.customer = customerResponse;

      const transactionsResponse = await this.fetchTransactionByCustomerId(
        this.id
      );
      this.transactions = transactionsResponse?.data || [];

      const couponsResponse = await this.fetchCouponTransactionByCustomerId(
        this.id
      );
      this.coupons = couponsResponse?.data || [];
    } catch (error) {
      this.error = "Failed to fetch customer data. Please try again later.";
      console.error("Error fetching customer data:", error);
    } finally {
      this.loading = false;
    }
  },
  computed: {
    ...mapGetters("branches", ["allBranches"]),
    customerDetails() {
      return [
        { label: "Full Name", value: this.customer?.name || "-" },
        { label: "Email", value: this.customer?.email || "-" },
        { label: "Phone Number", value: this.customer?.phone_number || "-" },
        { label: "Address", value: this.customer?.address || "-" },
        {
          label: "Branch",
          value: this.getBranchName(this.customer?.branch_id) || "-",
        },
      ];
    },

    availableCoupons() {
      const now = new Date();
      return this.coupons.filter(
        (coupon) => new Date(coupon.expired_date) >= now
      );
    },
    expiredCoupons() {
      const now = new Date();
      return this.coupons.filter(
        (coupon) => new Date(coupon.expired_date) < now
      );
    },
  },
  methods: {
    formatDate,
    formatRupiah,
    ...mapActions("customers", ["fetchCustomerById"]),
    ...mapActions("transactions", ["fetchTransactionByCustomerId"]),
    ...mapActions("coupon_transactions", [
      "fetchCouponTransactionByCustomerId",
    ]),

    getBranchName(branchId) {
      const branch = this.allBranches.find((b) => b.id === branchId);
      return branch ? branch.name : "-";
    },

    isCouponExpired(expiredDate) {
      const now = new Date();
      const expired = new Date(expiredDate);
      return expired < now;
    },
  },
};
</script>

<style scoped>
/* You can add some custom styling here if needed */
</style>
